/*
  Take a Ministry Checkup survey. Save progress, submit when done.
*/
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { get, post, getErrorMessage } from '../api/client';

const RESPONSE_OPTIONS = ['Yes', 'No', 'Needs Improvement', 'N/A', 'Not Observed'];

const MinistryCheckupTakePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [template, setTemplate] = useState(null);
  const [existing, setExisting] = useState(null);
  const [responses, setResponses] = useState({});
  const [finalComment, setFinalComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [tRes, sRes, rRes] = await Promise.all([
          get('/api/surveys/template'),
          get(`/api/surveys/${id}`),
          get(`/api/surveys/${id}/my-response`),
        ]);
        if (tRes.ok) setTemplate(tRes.data);
        if (sRes.ok) setSurvey(sRes.data);
        if (rRes.ok) {
          setExisting(rRes.data);
          const map = {};
          (rRes.data.responses || []).forEach((r) => {
            map[r.questionKey] = {
              answer: r.answer,
              comment: r.comment,
              itemAnswers: Array.isArray(r.itemAnswers) ? [...r.itemAnswers] : undefined,
            };
          });
          setResponses(map);
          setFinalComment(rRes.data.finalComment || '');
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load survey');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetch();
  }, [id]);

  const updateResponse = (questionKey, field, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionKey]: {
        ...(prev[questionKey] || {}),
        [field]: value,
      },
    }));
  };

  const updateItemAnswer = (questionKey, itemIndex, value) => {
    setResponses((prev) => {
      const curr = prev[questionKey] || {};
      const itemAnswers = [...(curr.itemAnswers || [])];
      while (itemAnswers.length <= itemIndex) itemAnswers.push('');
      itemAnswers[itemIndex] = value;
      const q = template?.sections?.flatMap((s) => s.questions || []).find((qu) => qu.key === questionKey);
      const answer = computeChecklistAnswer(itemAnswers, q?.items?.length || 0);
      return {
        ...prev,
        [questionKey]: { ...curr, itemAnswers, answer },
      };
    });
  };

  const computeChecklistAnswer = (itemAnswers, itemCount) => {
    if (!itemCount || !Array.isArray(itemAnswers)) return null;
    let presentCount = 0, absentCount = 0;
    for (let i = 0; i < Math.min(itemAnswers.length, itemCount); i++) {
      const v = (itemAnswers[i] || '').toLowerCase();
      if (v === 'present') presentCount += 1;
      else if (v === 'absent') absentCount += 1;
    }
    const scoredCount = presentCount + absentCount;
    if (scoredCount === 0) return 'N/A';
    if (presentCount === scoredCount) return 'Yes';
    if (presentCount > 0) return 'Needs Improvement';
    return 'No';
  };

  const buildResponsesArr = () =>
    Object.entries(responses).map(([questionKey, v]) => ({
      questionKey,
      answer: v?.answer || null,
      comment: v?.comment || '',
      itemAnswers: Array.isArray(v?.itemAnswers) ? v.itemAnswers : undefined,
    }));

  const saveProgress = async () => {
    setSaving(true);
    setError('');
    const { ok, status, data } = await post(`/api/surveys/${id}/responses`, {
      responses: buildResponsesArr(),
      finalComment,
      submit: false,
    });
    setSaving(false);
    if (!ok) setError(getErrorMessage({ status, data }, 'Failed to save'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const { ok, status, data } = await post(`/api/surveys/${id}/responses`, {
      responses: buildResponsesArr(),
      finalComment,
      submit: true,
    });
    setSaving(false);
    if (ok) {
      navigate('/ministry-checkup', { state: { submitted: true } });
    } else {
      setError(getErrorMessage({ status, data }, 'Failed to submit'));
    }
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading…</div>;
  if (!survey || !template) return <div style={{ padding: '2rem' }}>Survey not found.</div>;

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 1rem 6rem 1rem' }}>
      <PageHeader title={survey.title} backTo="/ministry-checkup" backLabel="← Ministry Checkup" />
      <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
        Rate each item (Yes, No, Needs Improvement, N/A, or Not Observed). Add comments when helpful. Save progress anytime—submit when done.
      </p>

      {existing?.status === 'submitted' && (
        <p style={{ color: 'var(--cr-text)', marginBottom: '1rem', fontWeight: 500 }}>You have already submitted this survey.</p>
      )}

      <form onSubmit={handleSubmit}>
        {(template.sections || []).map((section) => (
          <div key={section.key} style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '0.75rem', color: 'var(--cr-text)', fontSize: '1.15rem' }}>{section.title}</h3>
            <div style={{ padding: '1rem', background: 'var(--cr-bg-warm)', borderRadius: '8px', border: '1px solid var(--cr-border)' }}>
              {(section.questions || []).map((q) => (
                <div key={q.key} style={{ marginBottom: '1.25rem' }}>
                  <div style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
                    {q.text}
                    {q.items && !q.checklist && (
                      <ul style={{ margin: '0.35rem 0 0 1.25rem', paddingLeft: '0.5rem' }}>
                        {q.items.map((item, i) => (
                          <li key={i} style={{ marginBottom: '0.2rem' }}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {q.checklist && q.items ? (
                    <>
                      <p style={{ fontSize: '0.85rem', color: 'var(--cr-text-muted)', marginBottom: '0.5rem' }}>
                        For each item: Present = fully there, Absent = missing, N/A = not applicable. All present = Yes; some present = Needs Improvement; none = No.
                      </p>
                      <div style={{ marginBottom: '0.75rem' }}>
                        {(q.items || []).map((item, i) => {
                          const itemAnswers = responses[q.key]?.itemAnswers || [];
                          const val = (itemAnswers[i] || '').toLowerCase();
                          return (
                            <div key={i} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem', padding: '0.35rem 0', borderBottom: '1px solid var(--cr-border)' }}>
                              <span style={{ flex: '1 1 200px', minWidth: 0 }}>{item}</span>
                              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                                {['present', 'absent', 'na'].map((opt) => (
                                  <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                                    <input
                                      type="radio"
                                      name={`${q.key}-${i}`}
                                      value={opt}
                                      checked={val === opt}
                                      onChange={() => updateItemAnswer(q.key, i, opt)}
                                      disabled={existing?.status === 'submitted'}
                                    />
                                    <span>{opt === 'na' ? 'N/A' : opt.charAt(0).toUpperCase() + opt.slice(1)}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      {RESPONSE_OPTIONS.map((opt) => (
                        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name={q.key}
                            value={opt}
                            checked={(responses[q.key]?.answer || '') === opt}
                            onChange={() => updateResponse(q.key, 'answer', opt)}
                            disabled={existing?.status === 'submitted'}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {q.items && q.checklist && (
                    <div style={{ fontSize: '0.85rem', color: 'var(--cr-text-muted)', marginBottom: '0.5rem' }}>
                      Overall: <strong>{(responses[q.key]?.answer || '—')}</strong>
                    </div>
                  )}
                  <textarea
                    placeholder="Comments (optional)"
                    value={responses[q.key]?.comment || ''}
                    onChange={(e) => updateResponse(q.key, 'comment', e.target.value)}
                    disabled={existing?.status === 'submitted'}
                    spellCheck
                    rows={2}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      padding: '0.35rem 0.5rem',
                      borderRadius: '6px',
                      border: '1px solid var(--cr-border)',
                      fontSize: '0.95rem',
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}

        {template.finalQuestion && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '0.75rem', color: 'var(--cr-text)', fontSize: '1.15rem' }}>{template.finalQuestion.text}</h3>
            <textarea
              placeholder="Add your thoughts..."
              value={finalComment}
              onChange={(e) => setFinalComment(e.target.value)}
              disabled={existing?.status === 'submitted'}
              spellCheck
              rows={4}
              style={{
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                padding: '0.5rem',
                borderRadius: '8px',
                border: '1px solid var(--cr-border)',
                fontSize: '1rem',
              }}
            />
          </div>
        )}

        {error && <p style={{ color: 'var(--cr-error)', marginBottom: '0.5rem' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
          {existing?.status !== 'submitted' && (
            <>
              <button type="button" className="btn-outline" onClick={saveProgress} disabled={saving}>
                {saving ? 'Saving…' : 'Save Progress'}
              </button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Submitting…' : 'Submit Survey'}
              </button>
            </>
          )}
          <button type="button" className="btn-outline" onClick={() => navigate('/ministry-checkup')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default MinistryCheckupTakePage;
